<template>
    <div class="pc">
        <UserTopNav :active="'projects'" />

        <div class="pc-container" v-if="scopedProjectData">
            <!-- ═══ Top bar ═══ -->
            <div class="pc-topbar">
                <router-link
                    :to="{
                        name: 'ProjectDetails',
                        params: {
                            projectId: currentProjectId,
                            projectSlug: currentProjectSlug,
                        },
                    }"
                    class="pc-back"
                >
                    <font-awesome-icon icon="fa-solid fa-arrow-left" />
                    <span>Inapoi la Proiect</span>
                </router-link>

                <div class="pc-topbar__actions">
                    <a
                        v-if="scopedProjectData.template_type == 'simple'"
                        class="pc-action-btn pc-action-btn--rainbow"
                        target="_blank"
                        :href="`${getURL()}/#/ps/${scopedProjectData.project_id}/${slugify(scopedProjectData.project_name)}`"
                    >
                        <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" />
                        <span>Editeaza Invitatia</span>
                    </a>
                    <a
                        v-else
                        class="pc-action-btn pc-action-btn--rainbow"
                        target="_blank"
                        :href="`${getURL()}/#/public/${scopedProjectData.project_id}/edit`"
                    >
                        <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" />
                        <span>Editeaza Invitatia</span>
                    </a>

                    <button
                        v-if="scopedProjectData.template_type == 'simple'"
                        class="pc-action-btn pc-action-btn--ghost"
                        @click="copyToClipboard(`${getURL()}/#/ps/${scopedProjectData.project_id}/${slugify(scopedProjectData.project_name)}`)"
                    >
                        <font-awesome-icon icon="fa-solid fa-copy" />
                        <span>Copiaza Link</span>
                    </button>

                </div>
            </div>

            <h1 class="pc-title">Configuratii</h1>
            <p class="pc-subtitle">Detalii eveniment si setari proiect.</p>

            <!-- ═══ Event Details — full width ═══ -->
            <section class="pc-card">
                <div class="pc-card__header">
                    <div class="pc-card__icon pc-card__icon--blue">
                        <font-awesome-icon icon="fa-solid fa-calendar-days" />
                    </div>
                    <div>
                        <h2 class="pc-card__title">Evenimentul tau</h2>
                        <p class="pc-card__desc">Detaliile proiectului curent.</p>
                    </div>
                </div>

                <div class="pc-card__body">
                    <div class="pc-details-grid">
                        <!-- Editable: Nume proiect -->
                        <div class="pc-info-row" :class="{ 'pc-info-row--editable': canEdit }">
                            <span class="pc-info-row__label">Nume proiect</span>
                            <div class="pc-info-row__right" v-if="!editingName">
                                <span class="pc-info-row__value">{{ scopedProjectData.project_name }}</span>
                                <button v-if="canEdit" class="pc-edit-btn" @click="startEditName" data-tip="Modifică">
                                    <font-awesome-icon icon="fa-solid fa-pen" />
                                </button>
                                <font-awesome-icon v-if="!canEdit && editStatusLoaded" icon="fa-solid fa-lock" class="pc-lock-icon" />
                            </div>
                            <div class="pc-edit-inline" v-else>
                                <input
                                    v-model="editName"
                                    class="pc-edit-input"
                                    type="text"
                                    maxlength="100"
                                    @keyup.enter="saveProjectDetails"
                                    @keyup.escape="cancelEditName"
                                    ref="nameInput"
                                />
                                <button class="pc-edit-save" @click="saveProjectDetails" :disabled="saving">
                                    <font-awesome-icon v-if="saving" icon="fa-solid fa-spinner" spin />
                                    <font-awesome-icon v-else icon="fa-solid fa-check" />
                                </button>
                                <button class="pc-edit-cancel" @click="cancelEditName">
                                    <font-awesome-icon icon="fa-solid fa-xmark" />
                                </button>
                            </div>
                        </div>

                        <div class="pc-info-row">
                            <span class="pc-info-row__label">Status</span>
                            <span
                                v-if="scopedProjectData.active == 1"
                                class="pc-badge pc-badge--green"
                            >Activ</span>
                            <span v-else class="pc-badge pc-badge--red">
                                Inactiv
                                <router-link to="/pachete" class="pc-link" style="margin-left: 8px;">Activeaza</router-link>
                            </span>
                        </div>

                        <div class="pc-info-row">
                            <span class="pc-info-row__label">Template</span>
                            <span class="pc-info-row__value">{{ scopedProjectData.project_template }}</span>
                        </div>

                        <div class="pc-info-row">
                            <span class="pc-info-row__label">Tip proiect</span>
                            <span class="pc-info-row__value" style="text-transform: capitalize;">{{ scopedProjectData.template_type }}</span>
                        </div>

                        <!-- Editable: Tip eveniment -->
                        <div class="pc-info-row pc-info-row--editable">
                            <span class="pc-info-row__label">Tip eveniment</span>
                            <div class="pc-info-row__right" v-if="!editingEventType">
                                <span class="pc-info-row__value">{{ scopedProjectData.event_type_label || 'Neselectat' }}</span>
                                <button class="pc-edit-btn" @click="startEditEventType" data-tip="Modifică">
                                    <font-awesome-icon icon="fa-solid fa-pen" />
                                </button>
                            </div>
                            <div class="pc-edit-inline" v-else>
                                <select v-model="editEventTypeId" class="pc-edit-input">
                                    <option :value="null">— Neselectat —</option>
                                    <option v-for="et in eventTypes" :key="et.id" :value="et.id">{{ et.label }}</option>
                                </select>
                                <button class="pc-edit-save" @click="saveEventType" :disabled="savingEventType">
                                    <font-awesome-icon v-if="savingEventType" icon="fa-solid fa-spinner" spin />
                                    <font-awesome-icon v-else icon="fa-solid fa-check" />
                                </button>
                                <button class="pc-edit-cancel" @click="cancelEditEventType">
                                    <font-awesome-icon icon="fa-solid fa-xmark" />
                                </button>
                            </div>
                        </div>

                        <!-- Editable: Data eveniment -->
                        <div class="pc-info-row" :class="{ 'pc-info-row--editable': canEdit }">
                            <span class="pc-info-row__label">Data eveniment</span>
                            <div class="pc-info-row__right" v-if="!editingDate">
                                <span class="pc-info-row__value">{{ scopedProjectData.project_date }}</span>
                                <button v-if="canEdit" class="pc-edit-btn" @click="startEditDate" data-tip="Modifică">
                                    <font-awesome-icon icon="fa-solid fa-pen" />
                                </button>
                                <font-awesome-icon v-if="!canEdit && editStatusLoaded" icon="fa-solid fa-lock" class="pc-lock-icon" />
                            </div>
                            <div class="pc-edit-inline" v-else>
                                <input
                                    v-model="editDate"
                                    class="pc-edit-input"
                                    type="date"
                                    @keyup.enter="saveProjectDetails"
                                    @keyup.escape="cancelEditDate"
                                    ref="dateInput"
                                />
                                <button class="pc-edit-save" @click="saveProjectDetails" :disabled="saving">
                                    <font-awesome-icon v-if="saving" icon="fa-solid fa-spinner" spin />
                                    <font-awesome-icon v-else icon="fa-solid fa-check" />
                                </button>
                                <button class="pc-edit-cancel" @click="cancelEditDate">
                                    <font-awesome-icon icon="fa-solid fa-xmark" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Locked notice: simple project -->
                    <div v-if="editStatusLoaded && !canEdit && lockReason === 'viewed'" class="pc-notice pc-notice--info" style="margin-top: 14px;">
                        <font-awesome-icon icon="fa-solid fa-lock" />
                        <div>
                            <strong>Numele și data evenimentului nu mai pot fi modificate</strong>
                            <p style="margin: 4px 0 0; font-size: 12px; line-height: 1.5;">Invitația a fost vizualizată de {{ publicViews }} {{ publicViews === 1 ? 'persoană' : 'ori' }}. Modificarea acestor date ar crea inconsistențe cu ce au văzut invitații și ar putea afecta procesele automate (GDPR).</p>
                        </div>
                    </div>

                    <!-- Locked notice: complex project -->
                    <div v-if="editStatusLoaded && !canEdit && lockReason === 'sent'" class="pc-notice pc-notice--info" style="margin-top: 14px;">
                        <font-awesome-icon icon="fa-solid fa-lock" />
                        <div>
                            <strong>Numele și data evenimentului nu mai pot fi modificate</strong>
                            <p style="margin: 4px 0 0; font-size: 12px; line-height: 1.5;">Invitațiile au fost deja trimise ({{ invitationsSentSummary }}). Modificarea acestor date ar crea inconsistențe cu mesajele primite de invitați și ar putea afecta procesele automate (GDPR, remindere).</p>
                        </div>
                    </div>

                    <!-- Editable notice: simple project -->
                    <div v-if="editStatusLoaded && canEdit && scopedProjectData.template_type === 'simple'" class="pc-notice pc-notice--green" style="margin-top: 14px;">
                        <font-awesome-icon icon="fa-solid fa-pen" />
                        <div>
                            <strong>Poți modifica numele și data evenimentului</strong>
                            <p style="margin: 4px 0 0; font-size: 12px; line-height: 1.5;">Invitația nu a fost vizualizată de nimeni încă. După prima vizualizare, aceste câmpuri vor fi blocate automat.</p>
                        </div>
                    </div>

                    <!-- Editable notice: complex project -->
                    <div v-if="editStatusLoaded && canEdit && scopedProjectData.template_type !== 'simple'" class="pc-notice pc-notice--green" style="margin-top: 14px;">
                        <font-awesome-icon icon="fa-solid fa-pen" />
                        <div>
                            <strong>Poți modifica numele și data evenimentului</strong>
                            <p style="margin: 4px 0 0; font-size: 12px; line-height: 1.5;">Nicio invitație nu a fost trimisă încă. După prima trimitere, aceste câmpuri vor fi blocate pentru a păstra consistența datelor.</p>
                        </div>
                    </div>

                    <div v-if="scopedProjectData.active == 0" class="pc-notice pc-notice--warn" style="margin-top: 14px;">
                        <font-awesome-icon icon="fa-solid fa-circle-info" />
                        <span>Proiectul tau este inactiv — invitatia se poate edita dar nu se poate partaja.</span>
                    </div>
                </div>
            </section>

            <!-- ═══ Danger Zone ═══ -->
            <section class="pc-card pc-card--danger" v-if="scopedProjectData && scopedProjectData.project_id">
                <div class="pc-card__header">
                    <div class="pc-card__icon pc-card__icon--red">
                        <font-awesome-icon icon="fa-solid fa-trash-can" />
                    </div>
                    <div>
                        <h2 class="pc-card__title">Zona periculoasa</h2>
                        <p class="pc-card__desc">Sterge proiectul si toate datele asociate. Aceasta actiune este ireversibila.</p>
                    </div>
                </div>
                <div class="pc-card__body">
                    <DeleteProject
                        :projectId="scopedProjectData.project_id"
                    ></DeleteProject>
                </div>
            </section>
        </div>

        <div v-if="loading" class="pc-loading">
            <LoadingComponent></LoadingComponent>
        </div>
    </div>
</template>

<script>
import DeleteProject from '@/components/DeleteProject.vue';
import LoadingComponent from '@/components/LoadingComponent.vue';
import eventBus from '@/utils/EventBus';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import UserTopNav from '@/components/UserTopNav.vue';
import { mapGetters } from 'vuex';

export default {
    name: 'ProjectDetailsConfig',
    props: {
        projectId: {},
        projectName: {
            type: String,
        },
        projectSlug: {
            type: String,
        },
    },
    components: {
        DeleteProject,
        LoadingComponent,
        UserTopNav,
    },
    data() {
        return {
            currentProjectId: null,
            // Edit state
            editStatusLoaded: false,
            canEdit: false,
            lockReason: '',
            publicViews: 0,
            invitationsSentEmail: 0,
            invitationsSentSms: 0,
            editingName: false,
            editingDate: false,
            editName: '',
            editDate: '',
            saving: false,
            // Event type
            eventTypes: [],
            editingEventType: false,
            editEventTypeId: null,
            savingEventType: false,
        };
    },
    beforeUnmount() {
        eventBus.off('ProjectDeleted');
    },
    mounted() {
        this.$store.dispatch('fetchUserDetails');

        const projectId = this.$route.params.projectId;
        const options = {};
        this.$store.dispatch('fetchProjectUsers', {
            projectId,
            options,
            participants: true,
        });

        this.loadEditStatus();
        this.fetchEventTypes();
    },
    created() {
        // Access the current route from the Vue Router
        const currentRoute = this.$route;

        // Access the project ID from the route parameters
        this.currentProjectId = currentRoute.params.projectId;
    },
    setup() {
        // Get toast interface
        const toast = useToast();

        // Make it available inside methods
        return { toast };
    },
    computed: {
        ...mapGetters([
            'user',
            'projects',
            'plan',
            'credit',
            'loading',
            'scopedProjectData',
        ]),
        currentProjectName() {
            if (this.scopedProjectData && this.scopedProjectData.project_name) {
                return this.scopedProjectData.project_name;
            }
            return this.$route.params.projectSlug || '...';
        },
        currentProjectSlug() {
            if (this.scopedProjectData && this.scopedProjectData.project_slug) {
                return this.scopedProjectData.project_slug;
            }
            return this.$route.params.projectSlug || '';
        },
        invitationsSentSummary() {
            const parts = [];
            if (this.invitationsSentEmail > 0) parts.push(`${this.invitationsSentEmail} email`);
            if (this.invitationsSentSms > 0) parts.push(`${this.invitationsSentSms} SMS`);
            return parts.join(' + ') || 'trimise';
        },
    },
    methods: {
        slugify(str) {
            return (str || '')
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9-]+/g, '-')
                .replace(/^-+|-+$/g, '');
        },
        async loadEditStatus() {
            try {
                const jwtToken = localStorage.getItem('userToken');
                const res = await axios.get(
                    `/api/index.php?endpoint=updateProject&project_id=${this.currentProjectId}`,
                    { headers: { Authorization: `Bearer ${jwtToken}` } }
                );
                if (res.data.success) {
                    this.canEdit = !res.data.locked;
                    this.lockReason = res.data.lock_reason || '';
                    this.publicViews = res.data.public_views || 0;
                    this.invitationsSentEmail = res.data.email_sent_count || 0;
                    this.invitationsSentSms = res.data.sms_sent_count || 0;
                    this.editStatusLoaded = true;
                }
            } catch (e) {
                console.error('Error loading edit status:', e);
            }
        },
        startEditName() {
            this.editName = this.scopedProjectData.project_name;
            this.editingName = true;
            this.$nextTick(() => {
                if (this.$refs.nameInput) this.$refs.nameInput.focus();
            });
        },
        cancelEditName() {
            this.editingName = false;
            this.editName = '';
        },
        startEditDate() {
            this.editDate = this.scopedProjectData.project_date;
            this.editingDate = true;
            this.$nextTick(() => {
                if (this.$refs.dateInput) this.$refs.dateInput.focus();
            });
        },
        cancelEditDate() {
            this.editingDate = false;
            this.editDate = '';
        },
        async saveProjectDetails() {
            this.saving = true;
            try {
                const jwtToken = localStorage.getItem('userToken');
                const payload = { project_id: this.currentProjectId };

                if (this.editingName && this.editName.trim()) {
                    payload.project_name = this.editName.trim();
                }
                if (this.editingDate && this.editDate) {
                    payload.project_date = this.editDate;
                }

                const res = await axios.post(
                    '/api/index.php?endpoint=updateProject',
                    payload,
                    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` } }
                );

                if (res.data.success) {
                    this.toast.success(res.data.message);
                    // Refresh project data in Vuex
                    this.$store.dispatch('fetchProjectUsers', {
                        projectId: this.currentProjectId,
                        options: {},
                        participants: true,
                    });
                    this.editingName = false;
                    this.editingDate = false;
                } else {
                    this.toast.error(res.data.message || 'Eroare la salvare.');
                }
            } catch (e) {
                const msg = e.response?.data?.message || 'Eroare la salvare.';
                this.toast.error(msg);
                console.error(e);
            } finally {
                this.saving = false;
            }
        },
        async fetchEventTypes() {
            try {
                const jwtToken = localStorage.getItem('userToken');
                const res = await axios.get(
                    '/api/index.php?endpoint=eventTypes',
                    { headers: { Authorization: `Bearer ${jwtToken}` } }
                );
                if (res.data.success) {
                    this.eventTypes = res.data.data;
                }
            } catch (e) {
                console.error('Error fetching event types:', e);
            }
        },
        startEditEventType() {
            this.editEventTypeId = this.scopedProjectData.event_type_id || null;
            this.editingEventType = true;
        },
        cancelEditEventType() {
            this.editingEventType = false;
            this.editEventTypeId = null;
        },
        async saveEventType() {
            this.savingEventType = true;
            try {
                const jwtToken = localStorage.getItem('userToken');
                const payload = {
                    project_id: this.currentProjectId,
                    event_type_id: this.editEventTypeId ? parseInt(this.editEventTypeId) : null,
                };
                const res = await axios.post(
                    '/api/index.php?endpoint=updateProject',
                    payload,
                    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` } }
                );
                if (res.data.success) {
                    this.toast.success(res.data.message);
                    this.$store.dispatch('fetchProjectUsers', {
                        projectId: this.currentProjectId,
                        options: {},
                        participants: true,
                    });
                    this.editingEventType = false;
                } else {
                    this.toast.error(res.data.message || 'Eroare la salvare.');
                }
            } catch (e) {
                const msg = e.response?.data?.message || 'Eroare la salvare.';
                this.toast.error(msg);
                console.error(e);
            } finally {
                this.savingEventType = false;
            }
        },
        copyToClipboard(textToCopy) {
            console.log('textToCopy', textToCopy);

            // Fallback approach: Create a temporary DOM element
            const el = document.createElement('textarea');
            el.value = textToCopy;
            document.body.appendChild(el);
            el.select();

            // Check if the Clipboard API is supported
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard
                    .writeText(textToCopy)
                    .then(() => {
                        console.log('Text copied to clipboard: ' + textToCopy);
                        this.toast.success('Link-ul invitatiei a fost copiat!');
                    })
                    .catch((err) => {
                        console.error('Failed to copy text: ', err);
                    });
            } else {
                // Use the legacy approach
                try {
                    document.execCommand('copy');
                    // console.log(
                    //     'Text copied to clipboard (fallback): ' + textToCopy
                    // );
                    this.toast.success('Link-ul invitatiei a fost copiat!');
                } catch (err) {
                    console.error('Fallback: Failed to copy text: ', err);
                }
            }

            // Clean up by removing the temporary DOM element
            document.body.removeChild(el);
        },
        getURL() {
            // Get the current URL
            const currentURL = window.location.href;

            // Use URL API to parse the URL
            const url = new URL(currentURL);

            return url.origin;
        },
    },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════
   Layout
   ═══════════════════════════════════════════════ */
.pc {
    min-height: 100vh;
    background: #f8fafc;
}
.pc-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 20px 60px;
    text-align: left;
}
.pc-title {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 4px;
    letter-spacing: -0.3px;
}
.pc-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 24px;
}

/* ═══════════════════════════════════════════════
   Top bar
   ═══════════════════════════════════════════════ */
.pc-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 20px 0 24px;
    flex-wrap: wrap;
}
.pc-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    text-decoration: none;
    transition: color 0.15s;
}
.pc-back:hover { color: #0f172a; }
.pc-topbar__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

/* Action buttons */
.pc-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.15s;
    white-space: nowrap;
}
.pc-action-btn--rainbow {
    background: linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #d946ef, #ec4899, #f43f5e, #f97316, #eab308, #22c55e, #06b6d4, #3b82f6, #6366f1);
    background-size: 300% 300%;
    animation: rainbowShift 4s ease infinite;
    color: #fff;
    box-shadow: 0 2px 12px rgba(139, 92, 246, 0.3);
    font-weight: 700;
}
.pc-action-btn--rainbow:hover {
    box-shadow: 0 4px 20px rgba(139, 92, 246, 0.45);
    transform: translateY(-1px);
}
@keyframes rainbowShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}
.pc-action-btn--ghost {
    background: #f1f5f9;
    color: #475569;
}
.pc-action-btn--ghost:hover { background: #e2e8f0; color: #0f172a; }

/* ═══════════════════════════════════════════════
   Card (shared)
   ═══════════════════════════════════════════════ */
.pc-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.pc-card--danger {
    margin-top: 20px;
    border-left: 4px solid #ef4444;
}
.pc-card__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
}
.pc-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.pc-card__icon--violet { background: #ede9fe; color: #7c3aed; }
.pc-card__icon--blue { background: #dbeafe; color: #2563eb; }
.pc-card__icon--cyan { background: #cffafe; color: #0891b2; }
.pc-card__icon--green { background: #dcfce7; color: #16a34a; }
.pc-card__icon--red { background: #fee2e2; color: #ef4444; }
.pc-card__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
    text-align: left;
}
.pc-card__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
    text-align: left;
}
.pc-card__body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* Details grid (full-width, 2-col on desktop) */
.pc-details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

/* ═══════════════════════════════════════════════
   Info rows
   ═══════════════════════════════════════════════ */
.pc-info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
}
.pc-info-row__label {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.pc-info-row__value {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}
.pc-info-row__right {
    display: flex;
    align-items: center;
    gap: 8px;
}
.pc-info-row--editable {
    transition: border-color 0.15s;
}
.pc-info-row--editable:hover {
    border-color: #cbd5e1;
}

/* Edit inline */
.pc-edit-inline {
    display: flex;
    align-items: center;
    gap: 6px;
}
.pc-edit-input {
    padding: 5px 10px;
    border: 1.5px solid #cbd5e1;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
    min-width: 0;
    max-width: 200px;
}
.pc-edit-input:focus {
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}
.pc-edit-save {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: none;
    background: #dcfce7;
    color: #16a34a;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.15s;
}
.pc-edit-save:hover { background: #bbf7d0; }
.pc-edit-save:disabled { opacity: 0.5; cursor: not-allowed; }
.pc-edit-cancel {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: none;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.15s;
}
.pc-edit-cancel:hover { background: #e2e8f0; color: #0f172a; }
.pc-edit-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 7px;
    border: none;
    background: #ede9fe;
    color: #7c3aed;
    cursor: pointer;
    font-size: 11px;
    transition: all 0.15s;
}
.pc-edit-btn:hover { background: #ddd6fe; }
.pc-lock-icon {
    font-size: 13px;
    color: #94a3b8;
}

/* Badge */
.pc-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
}
.pc-badge--green { background: #dcfce7; color: #16a34a; }
.pc-badge--red { background: #fee2e2; color: #dc2626; }

/* Notice */
.pc-notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
}
.pc-notice--warn {
    background: #fffbeb;
    border: 1px solid #fef3c7;
    color: #92400e;
}
.pc-notice--info {
    background: #f0f4ff;
    border: 1px solid #c7d2fe;
    color: #3730a3;
}
.pc-notice--green {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
}

/* Link */
.pc-link {
    font-size: 12px;
    font-weight: 600;
    color: #2563eb;
    text-decoration: none;
}
.pc-link:hover { text-decoration: underline; }

/* ═══════════════════════════════════════════════
   Loading
   ═══════════════════════════════════════════════ */
.pc-loading {
    position: fixed;
    inset: 0;
    background: rgba(255,255,255,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
}

/* ═══════════════════════════════════════════════
   Mobile
   ═══════════════════════════════════════════════ */
@media (max-width: 768px) {
    .pc-container { padding: 0 16px 40px; }
    .pc-title { font-size: 22px; }
    .pc-topbar { flex-direction: column; align-items: flex-start; gap: 12px; }
    .pc-topbar__actions { width: 100%; flex-wrap: wrap; }
    .pc-action-btn { flex: 1; justify-content: center; min-width: 0; }
    .pc-details-grid { grid-template-columns: 1fr; }
    .pc-card { padding: 20px 18px; border-radius: 14px; }
    .pc-card__header { gap: 12px; margin-bottom: 20px; padding-bottom: 16px; }
    .pc-card__icon { width: 38px; height: 38px; font-size: 16px; }
}
</style>
